import { useMemo } from 'react'
import { createColumnHelper } from '@atbay/cyber-components'
import { TECHNOLOGY_TYPE_LABELS } from '@core/modules/digital-footprint/technologies/constants'
import {
    sortByLastSeen,
    sortByOpenIssuesCount,
    sortBySeverity,
    sortByTypeLabel,
} from '@core/modules/digital-footprint/technologies/technologies.filters'
import { SEVERITY_LEVELS } from '@core/modules/issues/constants'
import { formatDate } from '@core/utils/time'
import Show from '@/ui/components/helpers/Show'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'
import TableEmpty from '@/ui/components/table/table-empty'
import Typography from '@/ui/components/typography'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import {
    IssueCountItem,
    IssuesCell,
    SeverityCell,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import {
    getHighestSeverity,
    SEVERITY_TEXT_MAPPING,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { Cell } from './TechnologiesTable.styles'
import { useTechnologiesTable } from './useTechnologiesTable'

const columnHelper = createColumnHelper<TTechnology>()

const TechnologiesTable = () => {
    const {
        sortedData,
        issueCountsByName,
        openIssuesByName,
        sorting,
        setSorting,
        handleRowClick,
        activeTechnologyId,
    } = useTechnologiesTable()

    const emptyMessage =
        'No technologies available for the current search and filter settings'

    const columns = useMemo(
        () => [
            {
                id: 'severity',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const highestSeverity = getHighestSeverity(
                            info.row.original.issues
                        )

                        return (
                            <SeverityCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`technology-table-severity-${highestSeverity}`}
                            >
                                <SeverityIcon severity={highestSeverity} />
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="technology-severity"
                                >
                                    {SEVERITY_TEXT_MAPPING[highestSeverity]}
                                </Typography>
                            </SeverityCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Severity</Typography>
                    ),
                    enableSorting: true,
                    sortingFn: sortBySeverity,
                }),
                width: '15%',
            },
            {
                ...columnHelper.accessor('name', {
                    cell: (info) => (
                        <Cell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="technology-table-name"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId="technology-name"
                            >
                                {info.getValue()}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <Typography variant="s-regular">Name</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '20%',
            },
            {
                ...columnHelper.accessor('type', {
                    cell: (info) => (
                        <Cell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="technology-table-type"
                        >
                            <Typography
                                className="table-cell-text"
                                component="p"
                                variant="s-regular"
                                testId={`technology-type-${info.getValue()}`}
                            >
                                {TECHNOLOGY_TYPE_LABELS[info.getValue()]}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <Typography variant="s-regular">Source</Typography>
                    ),
                    enableSorting: true,
                    sortingFn: sortByTypeLabel,
                }),
                width: '17%',
            },
            {
                ...columnHelper.accessor('last_seen', {
                    cell: (info) => {
                        const value = info.getValue()
                        return (
                            <Cell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid="technology-table-last-seen"
                            >
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="technology-last-seen"
                                >
                                    {value ? formatDate(value) : '—'}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Last Seen</Typography>
                    ),
                    enableSorting: true,
                    sortingFn: sortByLastSeen,
                }),
                width: '16%',
            },
            {
                ...columnHelper.accessor('version', {
                    cell: (info) => (
                        <Cell
                            onClick={() => handleRowClick(info.row, info.table)}
                            data-testid="technology-table-version"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                                testId="technology-version"
                            >
                                {info.getValue() ?? '—'}
                            </Typography>
                        </Cell>
                    ),
                    header: () => (
                        <Typography variant="s-regular">Version</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '12%',
            },
            {
                id: 'openIssues',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const technology = info.row.original
                        const issuesCount = issueCountsByName[technology.name]

                        return (
                            <IssuesCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid="technology-table-open-issues"
                            >
                                {SEVERITY_LEVELS.map((severity) => (
                                    <Show
                                        key={severity}
                                        when={issuesCount?.[severity]}
                                    >
                                        <IssueCountItem
                                            className={`count ${severity}`}
                                            data-testid="technology-issues-severity-count"
                                        >
                                            <SeverityIcon
                                                severity={severity}
                                                dataTestId={`severity-${severity}`}
                                            />
                                            <Typography
                                                className="table-cell-text"
                                                variant="s-regular"
                                            >
                                                {issuesCount?.[severity]}
                                            </Typography>
                                        </IssueCountItem>
                                    </Show>
                                ))}
                            </IssuesCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Open Issues</Typography>
                    ),
                    enableSorting: true,
                    sortingFn: sortByOpenIssuesCount(openIssuesByName),
                }),
                width: '20%',
            },
        ],
        [issueCountsByName, openIssuesByName, handleRowClick]
    )

    return (
        <StyledCustomTable
            activeRow={activeTechnologyId}
            getRowId={(row: TTechnology) => row.id}
            tableHeight={700}
            columns={columns as any}
            data={sortedData}
            emptyState={<TableEmpty message={emptyMessage} />}
            setSorting={setSorting}
            sorting={sorting}
        />
    )
}

export default TechnologiesTable
